import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layers, Cpu, Droplets, ArrowRight, CheckCircle, Zap, DollarSign, Clock, ShieldCheck, ScanLine } from 'lucide-react';
export function HomePage() {
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.6
    }
  };
  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  return <div className="min-h-screen bg-sandev-bg" data-id="element-267">
      {/* Hero Section */}
      <section className="relative pt-24 pb-32 overflow-hidden" data-id="element-268">
        {/* Abstract background elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-sandev-accent/5 rounded-full blur-3xl -z-10" data-id="element-269"></div>
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-sandev-accent/10 rounded-full blur-3xl -z-10 translate-x-1/3 -translate-y-1/3" data-id="element-270"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-271">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-272">
            <motion.div className="flex-1 text-center lg:text-left" initial="initial" animate="animate" variants={staggerContainer} data-id="element-273">
              <motion.div variants={fadeIn} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sandev-card border border-sandev-border text-sandev-accent text-sm font-medium mb-6" data-id="element-274">
                <Zap size={14} data-id="element-275" />
                <span data-id="element-276">Next-Gen Prototyping</span>
              </motion.div>
              <motion.h1 variants={fadeIn} className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-sandev-text mb-6" data-id="element-277">
                Industrial Prototyping, <br data-id="element-278" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sandev-accent to-blue-500" data-id="element-279">
                  Made Accessible.
                </span>
              </motion.h1>
              <motion.p variants={fadeIn} className="text-lg md:text-xl text-sandev-muted mb-10 max-w-2xl mx-auto lg:mx-0" data-id="element-280">
                SanDev Industries provides professional design, FDM, high-detail
                resin 3D printing, and 3D scanning services. A faster, cheaper
                alternative to traditional manufacturing prototypes.
              </motion.p>
              <motion.div variants={fadeIn} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-281">
                <Link to="/contact" className="w-full sm:w-auto px-8 py-4 bg-sandev-accent hover:bg-sandev-accentHover text-white rounded-md font-semibold transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)] hover:shadow-[0_0_30px_rgba(0,180,216,0.5)] flex items-center justify-center gap-2" data-id="element-282">
                  Start Your Project <ArrowRight size={18} data-id="element-283" />
                </Link>
                <Link to="/materials" className="w-full sm:w-auto px-8 py-4 bg-sandev-card hover:bg-sandev-cardHover border border-sandev-border text-sandev-text rounded-md font-semibold transition-colors flex items-center justify-center" data-id="element-284">
                  Explore Materials
                </Link>
              </motion.div>
            </motion.div>

            <motion.div className="flex-1 w-full max-w-lg lg:max-w-none" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-id="element-285">
              <div className="relative aspect-square rounded-2xl bg-gradient-to-br from-sandev-card to-sandev-bg border border-sandev-border p-8 flex items-center justify-center shadow-2xl overflow-hidden group" data-id="element-286">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-10 mix-blend-overlay" data-id="element-287"></div>
                <img src="https://cdn.magicpatterns.com/uploads/rmRVKZFYhXZrX1RFCfWxiS/SanDev_Logo_v5.png" alt="SanDev Industries Logo" className="w-full h-full object-contain drop-shadow-[0_0_30px_rgba(0,0,0,0.8)] group-hover:scale-105 transition-transform duration-700" data-id="element-288" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-sandev-card border-y border-sandev-border" data-id="element-289">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-290">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-291">
            <h2 className="text-3xl md:text-4xl font-bold text-sandev-text mb-4" data-id="element-292">
              Core Capabilities
            </h2>
            <p className="text-sandev-muted text-lg" data-id="element-293">
              From concept to physical part, we offer end-to-end solutions for
              your prototyping needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-294">
            {/* Service 1 */}
            <motion.div whileHover={{
            y: -10
          }} className="bg-sandev-bg border border-sandev-border rounded-xl p-8 transition-all hover:border-sandev-accent/50 hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)]" data-id="element-295">
              <div className="w-14 h-14 rounded-lg bg-sandev-card border border-sandev-border flex items-center justify-center mb-6 text-sandev-accent" data-id="element-296">
                <Cpu size={28} data-id="element-297" />
              </div>
              <h3 className="text-xl font-bold text-sandev-text mb-3" data-id="element-298">
                CAD Design
              </h3>
              <p className="text-sandev-muted leading-relaxed mb-6" data-id="element-299">
                Transform your ideas or 2D sketches into production-ready 3D
                models optimized for additive manufacturing.
              </p>
              <ul className="space-y-2" data-id="element-300">
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-301">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-302" />{' '}
                  Reverse Engineering
                </li>
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-303">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-304" />{' '}
                  Design for Additive Mfg
                </li>
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-305">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-306" />{' '}
                  Tolerance Optimization
                </li>
              </ul>
            </motion.div>

            {/* Service 2 */}
            <motion.div whileHover={{
            y: -10
          }} className="bg-sandev-bg border border-sandev-border rounded-xl p-8 transition-all hover:border-sandev-accent/50 hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)] relative overflow-hidden" data-id="element-307">
              <div className="absolute top-0 right-0 w-32 h-32 bg-sandev-accent/5 rounded-bl-full -z-10" data-id="element-308"></div>
              <div className="w-14 h-14 rounded-lg bg-sandev-card border border-sandev-border flex items-center justify-center mb-6 text-sandev-accent" data-id="element-309">
                <Layers size={28} data-id="element-310" />
              </div>
              <h3 className="text-xl font-bold text-sandev-text mb-3" data-id="element-311">
                FDM Printing
              </h3>
              <p className="text-sandev-muted leading-relaxed mb-6" data-id="element-312">
                Cost-effective, durable parts using industrial-grade
                thermoplastics. Ideal for functional testing and jigs.
              </p>
              <ul className="space-y-2" data-id="element-313">
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-314">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-315" /> Large
                  Build Volumes
                </li>
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-316">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-317" />{' '}
                  Engineering Materials
                </li>
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-318">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-319" /> Rapid
                  Iteration
                </li>
              </ul>
            </motion.div>

            {/* Service 3 */}
            <motion.div whileHover={{
            y: -10
          }} className="bg-sandev-bg border border-sandev-border rounded-xl p-8 transition-all hover:border-sandev-accent/50 hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)]" data-id="element-320">
              <div className="w-14 h-14 rounded-lg bg-sandev-card border border-sandev-border flex items-center justify-center mb-6 text-sandev-accent" data-id="element-321">
                <Droplets size={28} data-id="element-322" />
              </div>
              <h3 className="text-xl font-bold text-sandev-text mb-3" data-id="element-323">
                Resin Printing
              </h3>
              <p className="text-sandev-muted leading-relaxed mb-6" data-id="element-324">
                Ultra-high resolution SLA/DLP printing for parts requiring
                exceptional detail and smooth surface finishes.
              </p>
              <ul className="space-y-2" data-id="element-325">
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-326">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-327" />{' '}
                  Micro-precision Details
                </li>
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-328">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-329" />{' '}
                  Injection-mold Quality
                </li>
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-330">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-331" />{' '}
                  Specialty Resins
                </li>
              </ul>
            </motion.div>

            {/* Service 4 */}
            <motion.div whileHover={{
            y: -10
          }} className="bg-sandev-bg border border-sandev-border rounded-xl p-8 transition-all hover:border-sandev-accent/50 hover:shadow-[0_10px_30px_rgba(0,0,0,0.5)]" data-id="element-332">
              <div className="w-14 h-14 rounded-lg bg-sandev-card border border-sandev-border flex items-center justify-center mb-6 text-sandev-accent" data-id="element-333">
                <ScanLine size={28} data-id="element-334" />
              </div>
              <h3 className="text-xl font-bold text-sandev-text mb-3" data-id="element-335">
                3D Scanning
              </h3>
              <p className="text-sandev-muted leading-relaxed mb-6" data-id="element-336">
                Capture real-world objects as precise digital models for reverse
                engineering, quality inspection, or archival purposes.
              </p>
              <ul className="space-y-2" data-id="element-337">
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-338">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-339" />{' '}
                  Reverse Engineering
                </li>
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-340">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-341" />{' '}
                  Quality Inspection
                </li>
                <li className="flex items-center gap-2 text-sm text-sandev-muted" data-id="element-342">
                  <CheckCircle size={14} className="text-sandev-accent" data-id="element-343" />{' '}
                  Digital Archival
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-24" data-id="element-344">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-345">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-346">
            <div className="flex-1 grid grid-cols-2 gap-6" data-id="element-347">
              <div className="space-y-6 mt-12" data-id="element-348">
                <div className="bg-sandev-card p-6 rounded-xl border border-sandev-border" data-id="element-349">
                  <DollarSign className="text-sandev-accent mb-4" size={32} data-id="element-350" />
                  <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-351">
                    Cost Savings
                  </h4>
                  <p className="text-sm text-sandev-muted" data-id="element-352">
                    Fraction of the cost of traditional CNC or injection molding
                    prototypes.
                  </p>
                </div>
                <div className="bg-sandev-card p-6 rounded-xl border border-sandev-border" data-id="element-353">
                  <Clock className="text-sandev-accent mb-4" size={32} data-id="element-354" />
                  <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-355">
                    Fast Turnaround
                  </h4>
                  <p className="text-sm text-sandev-muted" data-id="element-356">
                    Go from digital file to physical part in days, not weeks or
                    months.
                  </p>
                </div>
              </div>
              <div className="space-y-6" data-id="element-357">
                <div className="bg-sandev-card p-6 rounded-xl border border-sandev-border" data-id="element-358">
                  <Layers className="text-sandev-accent mb-4" size={32} data-id="element-359" />
                  <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-360">
                    Material Variety
                  </h4>
                  <p className="text-sm text-sandev-muted" data-id="element-361">
                    Access to dozens of engineering-grade plastics and
                    specialized resins.
                  </p>
                </div>
                <div className="bg-sandev-card p-6 rounded-xl border border-sandev-border" data-id="element-362">
                  <ShieldCheck className="text-sandev-accent mb-4" size={32} data-id="element-363" />
                  <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-364">
                    Precision Quality
                  </h4>
                  <p className="text-sm text-sandev-muted" data-id="element-365">
                    Industrial machines calibrated for tight tolerances and
                    repeatable results.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex-1" data-id="element-366">
              <h2 className="text-3xl md:text-4xl font-bold text-sandev-text mb-6" data-id="element-367">
                Why Choose SanDev Over Traditional Methods?
              </h2>
              <p className="text-sandev-muted text-lg mb-8 leading-relaxed" data-id="element-368">
                Traditional manufacturing requires expensive tooling and long
                lead times just to see a first prototype. SanDev Industries
                eliminates these barriers.
              </p>
              <p className="text-sandev-muted text-lg mb-8 leading-relaxed" data-id="element-369">
                Whether you're an industrial manufacturer needing functional
                test parts, or a product designer requiring high-fidelity visual
                models, our additive manufacturing solutions provide the exact
                properties you need at a fraction of the traditional cost.
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 text-sandev-accent font-semibold hover:text-sandev-accentHover transition-colors" data-id="element-370">
                Learn more about our mission <ArrowRight size={18} data-id="element-371" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-sandev-card border-y border-sandev-border" data-id="element-372">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-373">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-374">
            <h2 className="text-3xl md:text-4xl font-bold text-sandev-text mb-4" data-id="element-375">
              Simple Process. Complex Parts.
            </h2>
            <p className="text-sandev-muted text-lg" data-id="element-376">
              Our streamlined workflow gets your parts in your hands faster.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative" data-id="element-377">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-sandev-border -translate-y-1/2 z-0" data-id="element-378"></div>

            {[{
            step: '01',
            title: 'Consult',
            desc: 'Discuss requirements, tolerances, and material needs.'
          }, {
            step: '02',
            title: 'Design/Prep',
            desc: 'CAD modeling or file optimization for printing.'
          }, {
            step: '03',
            title: 'Print',
            desc: 'Manufacturing using FDM or Resin technology.'
          }, {
            step: '04',
            title: 'Deliver',
            desc: 'Post-processing, quality check, and shipping.'
          }].map((item, index) => <div key={index} className="relative z-10 flex flex-col items-center text-center" data-id="element-379">
                <div className="w-16 h-16 rounded-full bg-sandev-bg border-2 border-sandev-accent text-sandev-accent flex items-center justify-center text-xl font-bold mb-6 shadow-[0_0_15px_rgba(0,180,216,0.2)]" data-id="element-380">
                  {item.step}
                </div>
                <h4 className="text-xl font-bold text-sandev-text mb-2" data-id="element-381">
                  {item.title}
                </h4>
                <p className="text-sandev-muted text-sm" data-id="element-382">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-id="element-383">
        <div className="absolute inset-0 bg-sandev-accent/5" data-id="element-384"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-385">
          <h2 className="text-3xl md:text-5xl font-bold text-sandev-text mb-6" data-id="element-386">
            Ready to prototype your next big idea?
          </h2>
          <p className="text-xl text-sandev-muted mb-10" data-id="element-387">
            Get a free quote and manufacturability analysis within 24 hours.
          </p>
          <Link to="/contact" className="inline-block px-10 py-5 bg-sandev-accent hover:bg-sandev-accentHover text-white rounded-md text-lg font-bold transition-all shadow-[0_0_20px_rgba(0,180,216,0.4)] hover:shadow-[0_0_40px_rgba(0,180,216,0.6)]" data-id="element-388">
            Request a Quote
          </Link>
        </div>
      </section>
    </div>;
}